import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  Copy,
  Check,
  Save,
  X as XIcon,
  Send,
  AlertTriangle,
  Flame,
  ExternalLink,
  Trash2,
} from "lucide-react";
import {
  subscribeLead,
  subscribeReplies,
  updateLeadStatus,
  deleteLead,
  categoryStyle,
  categoryLabel,
  normalizePlatformKey,
  platformLabel,
} from "../../services/collavueStore";
import { generateReply } from "../../services/collavueApi";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastProvider";
import PageHeader from "../../components/collavue/PageHeader";
import ConfirmDialog from "../../components/collavue/ConfirmDialog";
import { decodeHtmlEntities } from "../../utils/htmlEntities";
import { copyTextToClipboard } from "../../utils/browserSafety";

// Cost of one draft, mirrored from COLLAVUE_COSTS.generate_reply on the server
// and from the pricing table on the billing page.
const REPLY_CREDIT_COST = 1;

const MODES = [
  { v: "helpful", label: "Helpful answer", desc: "Value first, no brand mention." },
  { v: "soft_brand", label: "Soft brand", desc: "Helpful + subtle brand mention." },
  { v: "founder", label: "Founder-style", desc: "Personal, candid, first person." },
  { v: "direct_sales", label: "Direct sales", desc: "Use only when explicitly asked." },
  { v: "dm_opener", label: "DM opener", desc: "Short DM intro, no pitch." },
  { v: "no_brand", label: "No brand", desc: "Pure value, no CTA." },
];

const LENGTHS = [
  { v: "short", label: "Short" },
  { v: "long", label: "Long" },
];

export default function LeadDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { push } = useToast();
  const { userProfile } = useAuth();
  const credits = Number(userProfile?.credits ?? 0);
  const outOfCredits = credits < REPLY_CREDIT_COST;

  const [deleting, setDeleting] = useState(false);
  const [askDelete, setAskDelete] = useState(false);

  const [lead, setLead] = useState(null);
  // Distinguish "still loading" from "lead doesn't exist" — otherwise a
  // deleted lead or a stale link leaves the user on an infinite spinner.
  const [leadLoaded, setLeadLoaded] = useState(false);
  const [replies, setReplies] = useState([]);
  const [mode, setMode] = useState("helpful");
  const [length, setLength] = useState("short");
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const draftsRef = useRef(null);

  // Merge a reply into state without ever producing two entries for the same
  // draft. The generate call returns the new reply AND the Firestore listener
  // delivers it moments later; the old code blindly prepended the API result,
  // so whichever arrived second produced a duplicate card rendered under a
  // duplicate React key.
  const mergeReplies = useCallback((incoming) => {
    setReplies((prev) => {
      const byId = new Map();
      for (const r of [...(incoming || []), ...(Array.isArray(prev) ? prev : [])]) {
        const key = r?.id || r?.replyId;
        if (!key || byId.has(key)) continue;
        byId.set(key, r);
      }
      return [...byId.values()];
    });
  }, []);

  useEffect(() => {
    setLeadLoaded(false);
    setReplies([]);
    setGenError(null);
    const a = subscribeLead(id, (l) => {
      setLead(l);
      setLeadLoaded(true);
    });

    // Firestore is the source of truth for the draft list: it wins outright on
    // every snapshot so deleted/edited drafts can't linger in local state.
    const b = subscribeReplies(id, (incomingReplies) => {
      setReplies(Array.isArray(incomingReplies) ? incomingReplies : []);
    });

    return () => {
      a();
      b();
    };
  }, [id]);

  useEffect(() => {
    if (lead && lead.status === "unread") {
      updateLeadStatus(id, "read").catch(() => {});
    }
  }, [lead, id]);

  const onGenerate = async () => {
    // Guard the two states where the request is certain to fail server-side:
    // no lead loaded yet, and not enough credits to cover the draft.
    if (generating || !lead) return;
    if (outOfCredits) {
      setGenError({
        message: `You need ${REPLY_CREDIT_COST} credit to generate a draft.`,
        billing: true,
      });
      return;
    }

    setGenerating(true);
    setGenError(null);

    try {
      const r = await generateReply(id, mode, length);

      // Server-side field names are camelCase (that's what the Firestore
      // listener delivers); the API response nests the raw model output under
      // `draft` with snake_case keys. Write BOTH so this entry renders
      // identically whether it came from the API or from Firestore.
      const newReply = {
        id: r.replyId || `pending-${Date.now()}`,
        replyId: r.replyId || null,
        draft: r.draft || {},
        length: r.draft?.length || length,
        mode: r.draft?.mode || mode,
        content: r.draft?.reply || "",
        warnings: r.draft?.warnings || [],
        shouldReply: r.draft?.should_reply !== false,
        should_reply: r.draft?.should_reply,
        why: r.draft?.why || "",
        createdAt: new Date().toISOString(),
      };

      if (r.draft?.should_reply === false) {
        push({
          title: "Drafted with caution",
          desc: r.draft.why || "AI suggests not replying here.",
          variant: "error",
        });
      } else {
        push({ title: "Reply drafted", variant: "success" });
      }

      mergeReplies([newReply]);
      // The drafts sit below the fold on most screens, which made a successful
      // generation look like nothing happened.
      requestAnimationFrame(() => {
        draftsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (e) {
      const insufficient = e?.code === "INSUFFICIENT_CREDITS" || e?.status === 402;
      setGenError({
        message:
          e?.message ||
          "Something went wrong generating that draft. Please try again.",
        billing: insufficient,
      });
      push({
        title: insufficient ? "Not enough credits" : "Generation failed",
        desc: e?.message,
        variant: "error",
      });
    } finally {
      setGenerating(false);
    }
  };

  const copy = async (text, replyId) => {
    const ok = await copyTextToClipboard(text || "");
    if (ok) {
      setCopiedId(replyId);
      setTimeout(() => setCopiedId(null), 1500);
    } else {
      push({
        title: "Copy failed",
        desc: "Select the reply text and copy it manually.",
        variant: "error",
      });
    }
  };

  const setStatus = async (s) => {
    try {
      await updateLeadStatus(id, s);
      push({ title: `Lead ${s}`, variant: "success" });
    } catch (e) {
      push({ title: "Update failed", desc: e.message, variant: "error" });
    }
  };

  const onDelete = async () => {
    setDeleting(true);

    try {
      await deleteLead(id);
      push({ title: "Lead deleted", variant: "success" });
      nav("/leads");
    } catch (e) {
      push({ title: "Delete failed", desc: e.message, variant: "error" });
      setDeleting(false);
      setAskDelete(false);
    }
  };

  if (!lead && !leadLoaded) {
    return (
      <div className="p-10 text-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin inline" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <PageHeader
          title="Lead not found"
          subtitle="It may have been deleted, or the link is out of date."
        />
        <Link
          to="/leads"
          className="text-sm text-indigo-600 hover:underline inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Back to lead inbox
        </Link>
      </div>
    );
  }

  const breakdown = lead.scoreBreakdown || {};

  const platformKey = normalizePlatformKey(lead.platform);

  // What to call the thing being replied to, per source.
  const postNoun =
    platformKey === "x" ? "tweet" : platformKey === "reddit" ? "thread" : "post";

  const openOriginalLabel =
    platformKey === "x"
      ? "Open tweet"
      : platformKey === "reddit"
        ? "Open thread"
        : "Open original";

  const safeReplies = Array.isArray(replies) ? replies : [];

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 bg-[#fafafa] min-h-screen font-sans text-[#0B1038]">
      <ConfirmDialog
        open={askDelete}
        title="Delete this lead?"
        body="It will be removed from your inbox and cannot be undone."
        confirmLabel="Delete lead"
        variant="danger"
        loading={deleting}
        onConfirm={onDelete}
        onCancel={() => (deleting ? null : setAskDelete(false))}
      />

      <Link
        to="/leads"
        className="text-sm text-gray-600 hover:text-[#0B1038] inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft className="w-4 h-4" /> Lead inbox
      </Link>

      <PageHeader
        title={decodeHtmlEntities(lead.title) || "Lead"}
        subtitle={`${platformLabel(lead.platform)} · ${lead.community || ""}`}
        actions={
          <>
            <button
              onClick={() => setStatus("saved")}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50"
            >
              <Save className="w-4 h-4" /> Save
            </button>

            <button
              onClick={() => setStatus("contacted")}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm hover:bg-emerald-100"
            >
              <Send className="w-4 h-4" /> Mark contacted
            </button>

            <button
              onClick={() => setStatus("ignored")}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-500 hover:bg-slate-50"
            >
              <XIcon className="w-4 h-4" /> Ignore
            </button>

            <button
              onClick={() => setAskDelete(true)}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-rose-200 bg-white text-rose-700 text-sm hover:bg-rose-50 disabled:opacity-60"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Delete
            </button>
          </>
        }
      />

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span
                className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border ${categoryStyle(
                  lead.category
                )}`}
              >
                {categoryLabel(lead.category)}
              </span>

              {lead.url && (
                <a
                  href={lead.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
                >
                  {openOriginalLabel} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            <h2 className="text-lg font-semibold break-words">{decodeHtmlEntities(lead.title)}</h2>

            <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap break-words">
              {decodeHtmlEntities(lead.body)}
            </p>

            <div className="mt-4 text-xs text-slate-500 flex flex-wrap gap-3">
              {lead.authorHandle && <span>by @{lead.authorHandle}</span>}

              <span>
                Posted{" "}
                {lead.postedAt
                  ? new Date(lead.postedAt).toLocaleString()
                  : "—"}
              </span>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-600" />
                Generate a reply to this {postNoun}
              </h3>

              <span className="text-xs text-slate-500">
                {REPLY_CREDIT_COST} credit per draft · {credits} left
              </span>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div>
                <Label>Mode</Label>

                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  {MODES.map((m) => (
                    <option key={m.v} value={m.v}>
                      {m.label}
                    </option>
                  ))}
                </select>

                <p className="text-xs text-slate-500 mt-1">
                  {MODES.find((m) => m.v === mode)?.desc}
                </p>
              </div>

              <div>
                <Label>Length</Label>

                <div className="flex gap-2">
                  {LENGTHS.map((l) => (
                    <button
                      key={l.v}
                      type="button"
                      aria-pressed={length === l.v}
                      onClick={() => setLength(l.v)}
                      className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                        length === l.v
                          ? "bg-[#0B1038] text-white border-[#0B1038]"
                          : "border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Primary CTA of this page: full-width on mobile, brand navy
                (matching "Run sync" on the campaign page) rather than the
                indigo used for secondary links, and sized so it reads as the
                thing to click rather than another chip in the form. */}
            <button
              type="button"
              onClick={onGenerate}
              disabled={generating || !lead}
              aria-busy={generating}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#0B1038] text-white text-sm font-semibold shadow-sm hover:bg-[#151c54] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0B1038] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {generating
                ? "Generating…"
                : safeReplies.length > 0
                  ? "Regenerate reply"
                  : "Generate reply"}
              <span className="text-xs font-medium opacity-80">
                ({REPLY_CREDIT_COST} cr)
              </span>
            </button>

            {outOfCredits && !generating && (
              <p className="mt-2 text-xs text-amber-700">
                You're out of credits.{" "}
                <Link to="/billing" className="font-semibold underline">
                  Top up
                </Link>{" "}
                to generate drafts.
              </p>
            )}

            {genError && (
              <div
                role="alert"
                className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
              >
                {genError.message}
                {genError.billing && (
                  <>
                    {" "}
                    <Link to="/billing" className="font-semibold underline">
                      Buy credits
                    </Link>
                  </>
                )}
              </div>
            )}

            <div ref={draftsRef} className="mt-5 space-y-3 scroll-mt-6">
              {safeReplies.length === 0 && !generating && (
                <p className="text-sm text-slate-500">
                  No drafts yet. Pick a mode and click generate.
                </p>
              )}

              {generating && safeReplies.length === 0 && (
                <div className="border border-slate-200 rounded-xl p-4 animate-pulse space-y-2">
                  <div className="h-3 w-24 bg-slate-200 rounded" />
                  <div className="h-3 w-full bg-slate-200 rounded" />
                  <div className="h-3 w-4/5 bg-slate-200 rounded" />
                </div>
              )}

              {safeReplies.map((r, index) => {
                const replyId = r.id || r.replyId || `reply-${index}`;
                const draft = r.draft || {};
                const replyText = draft.reply || r.content || r.reply || "";
                const replyMode = draft.mode || r.mode || "";
                const replyLength = draft.length || r.length || "";
                const replyWarnings = draft.warnings || r.warnings || [];
                // Firestore stores `shouldReply`; the API response nests
                // `should_reply` under `draft`. Reading only the snake_case key
                // meant the "don't reply" warning silently vanished as soon as
                // the listener replaced the optimistic entry.
                const shouldReply =
                  draft.should_reply ?? r.should_reply ?? r.shouldReply;
                const why = draft.why || r.why || "";

                return (
                  <div
                    key={replyId}
                    className="border border-slate-200 rounded-xl p-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs mb-2">
                      <div className="text-slate-500">
                        <span className="font-semibold text-slate-700 capitalize">
                          {String(replyMode).replace("_", " ")}
                        </span>{" "}
                        · {replyLength}
                      </div>

                      <div className="flex items-center gap-2">
                        {shouldReply === false && (
                          <span className="inline-flex items-center gap-1 text-rose-600">
                            <AlertTriangle className="w-3 h-3" />
                            AI: don't reply
                          </span>
                        )}

                        <button
                          onClick={() => copy(replyText, replyId)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
                        >
                          {copiedId === replyId ? (
                            <Check className="w-3 h-3" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}{" "}
                          {copiedId === replyId ? "Copied" : "Copy"}
                        </button>

                        {lead.url && (
                          <a
                            href={lead.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                          >
                            {openOriginalLabel}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>

                    <p className="text-sm whitespace-pre-wrap break-words">{replyText}</p>

                    {replyWarnings.length > 0 && (
                      <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                        <strong>Warnings:</strong> {replyWarnings.join("; ")}
                      </div>
                    )}

                    {why && shouldReply === false && (
                      <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2 py-1.5">
                        {why}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold mb-3">Score</h3>

            <div className="flex items-center gap-4">
              <div className="text-4xl font-bold text-slate-900 inline-flex items-center gap-2">
                <Flame className="w-6 h-6 text-rose-500" />
                {lead.intentScore}
              </div>

              <div className="text-sm">
                <div className="text-amber-700 font-semibold">
                  Risk {lead.riskScore || 0}
                </div>

                <div className="text-slate-500 text-xs">
                  Recommended:{" "}
                  {String(lead.recommendedAction || "").replace(/_/g, " ")}
                </div>
              </div>
            </div>

            <ul className="mt-4 space-y-1.5 text-sm">
              {Object.entries(breakdown).map(([k, v]) => (
                <li
                  key={k}
                  className="flex items-center justify-between text-slate-700"
                >
                  <span className="capitalize">{k.replace(/_/g, " ")}</span>

                  <span
                    className={`font-semibold ${
                      v < 0 ? "text-rose-600" : "text-slate-900"
                    }`}
                  >
                    {v}
                  </span>
                </li>
              ))}
            </ul>

            {(lead.matchedKeywords || []).length > 0 && (
              <div className="mt-4">
                <div className="text-xs uppercase font-semibold text-slate-500 mb-1.5">
                  Matched keywords
                </div>

                <div className="flex flex-wrap gap-1">
                  {(lead.matchedKeywords || []).map((k, i) => (
                    <span
                      key={`${k}-${i}`}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100"
                    >
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {lead.matchReason && (
              <div className="mt-4 text-xs text-slate-600 border-t border-slate-100 pt-3">
                <strong>Why this matched:</strong> {decodeHtmlEntities(lead.matchReason)}
              </div>
            )}
          </div>

          {(lead.riskScore || 0) >= 40 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />

              <div>
                <strong>Caution.</strong> Negative signals present. Read the
                full thread before posting any reply.
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-2xl p-5 text-sm">
            <h3 className="font-semibold mb-2">Posting checklist</h3>

            <ul className="space-y-1.5 text-slate-700">
              <li>• Add value first; mention the brand only if appropriate.</li>
              <li>• Follow community rules and posting norms.</li>
              <li>• Disclose if you work for the company you mention.</li>
              <li>• Never copy a draft without reading it.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">
      {children}
    </label>
  );
}